import { useCallback } from 'react';

import type { FormInstance } from '@arco-design/web-react';

import type { VendorDeviceSelection } from '@/delivery-type-plugin/components';
import {
  VendorAndDeviceRadioList,
  VendorDeviceSelectionRequiredField,
  resolveVendorDeviceRequiredValidateField,
} from '@/delivery-type-plugin/components';

import { BARE_METAL_NODE_RESOURCE_CODE } from '../constants';
import type { BareMetalNodesBlockOptions, BareMetalNodesPaths } from '../types';
import { applyBillingNodeSelection, clearBillingNodeSelection } from '../utils/billingNodeMeta';

interface BillingNodeSelectorProps {
  billModelId: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  form: FormInstance;
  paths: BareMetalNodesPaths;
  disabled: boolean;
  skipFetch?: boolean;
  value: VendorDeviceSelection | null;
  onSelectionChange: (value: VendorDeviceSelection | null) => void;
  options?: BareMetalNodesBlockOptions;
}

/** 裸金属计价模式厂商设备校验字段前缀 */
const BARE_METAL_BILLING_VENDOR_DEVICE_PREFIX = 'bareMetalNodes';

/** 计价模式：厂商设备单选 */
export function BillingNodeSelector({
  billModelId,
  deploymentTypeId,
  form,
  paths,
  disabled,
  skipFetch,
  value,
  onSelectionChange,
  options,
}: BillingNodeSelectorProps) {
  const readBillingSelection = useCallback((): VendorDeviceSelection | null | undefined => {
    const optionMeta = form.getFieldValue('optionMeta') as Record<string, unknown> | undefined;
    return optionMeta?.node as VendorDeviceSelection | undefined;
  }, [form]);
  const requiredField = resolveVendorDeviceRequiredValidateField(BARE_METAL_BILLING_VENDOR_DEVICE_PREFIX);

  return (
    <VendorDeviceSelectionRequiredField fieldPrefix={BARE_METAL_BILLING_VENDOR_DEVICE_PREFIX} readSelection={readBillingSelection}>
      <VendorAndDeviceRadioList
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        resourceCode={BARE_METAL_NODE_RESOURCE_CODE}
        value={value}
        disabled={disabled}
        skipFetch={skipFetch}
        hideTitle
        onChange={(next) => {
          onSelectionChange(next);

          if (!next) {
            clearBillingNodeSelection(form, paths);
            options?.onChange?.('', undefined);
            void form.validate([requiredField]).catch(() => undefined);
            return;
          }

          applyBillingNodeSelection(form, paths, next);
          options?.onChange?.(next.billItemId || '', next);
          void form.validate([requiredField]).catch(() => undefined);
        }}
      />
    </VendorDeviceSelectionRequiredField>
  );
}
